import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { projectFormsAPI, projectVendorsAPI, projectTagsAPI, projectResponsesAPI } from '../../services/api';
import ReminderScheduleEditor, { buildReminderState, reminderStateToPayload, describeReminder } from './ReminderScheduleEditor';

/**
 * 問卷供應商指派面板
 * 顯示已指派供應商、截止日期、提醒設定、批量指派和發送功能
 */
export default function FormVendorPanel({ projectId, formId, formName }) {
  const [selectedVendors, setSelectedVendors] = useState(new Set());
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [bulkReminderVendorIds, setBulkReminderVendorIds] = useState(null); // 非 null 時開啟批量提醒視窗
  const [showBatchAssign, setShowBatchAssign] = useState(false);
  // 已指派供應商篩選/分頁狀態
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState('');
  const [assignmentPage, setAssignmentPage] = useState(1);
  const assignmentsPerPage = 10;
  // 回覆區域狀態
  const [showResponses, setShowResponses] = useState(true);
  const [responseFilter, setResponseFilter] = useState('');
  const [responsePage, setResponsePage] = useState(1);
  const responsesPerPage = 10;
  const queryClient = useQueryClient();

  // 取得已指派供應商列表
  const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['projects', projectId, 'forms', formId, 'assignments'],
    queryFn: () => projectFormsAPI.getAssignments(projectId, formId),
    staleTime: 60 * 1000,
  });

  // 取得所有供應商列表（用於指派）
  const { data: vendorsData } = useQuery({
    queryKey: ['projects', projectId, 'vendors'],
    queryFn: () => projectVendorsAPI.getAll(projectId),
    staleTime: 2 * 60 * 1000,
  });

  // 取得標籤列表（用於批量指派）
  const { data: tagsData } = useQuery({
    queryKey: ['projects', projectId, 'tags'],
    queryFn: () => projectTagsAPI.getAll(projectId),
    staleTime: 5 * 60 * 1000,
  });

  // 取得此問卷的回覆列表
  const { data: responsesData } = useQuery({
    queryKey: ['projects', projectId, 'responses', { formId }],
    queryFn: () => projectResponsesAPI.getAll(projectId, { formId }),
    staleTime: 60 * 1000,
  });

  // 取得問卷資料（包含 response_headers 欄位順序）
  const { data: formData } = useQuery({
    queryKey: ['projects', projectId, 'forms', formId],
    queryFn: () => projectFormsAPI.getOne(projectId, formId),
    staleTime: 60 * 1000,
  });

  const assignments = assignmentsData?.assignments || [];
  const allVendors = vendorsData?.vendors || [];
  const tags = tagsData?.tags || [];
  // 只列有效答案：重填前的舊版本不在這裡顯示（到「回覆追蹤」展開查看歷史版本）
  const responses = useMemo(
    () => (responsesData?.responses || []).filter((r) => r.status !== 'superseded'),
    [responsesData]
  );

  // 已指派供應商篩選和分頁
  const filteredAssignments = useMemo(() => {
    let result = assignments;
    if (assignmentSearch) {
      const search = assignmentSearch.toLowerCase();
      result = result.filter(a =>
        a.vendor_name?.toLowerCase().includes(search) ||
        a.vendor_email?.toLowerCase().includes(search) ||
        a.buyer?.toLowerCase().includes(search) ||
        a.business_unit?.toLowerCase().includes(search)
      );
    }
    if (assignmentStatusFilter) {
      result = result.filter(a => a.fill_status === assignmentStatusFilter);
    }
    return result;
  }, [assignments, assignmentSearch, assignmentStatusFilter]);

  const totalAssignmentPages = Math.ceil(filteredAssignments.length / assignmentsPerPage);
  const paginatedAssignments = filteredAssignments.slice(
    (assignmentPage - 1) * assignmentsPerPage,
    assignmentPage * assignmentsPerPage
  );

  // 回覆搜尋過濾和分頁
  const filteredResponses = useMemo(() => {
    if (!responseFilter) return responses;
    const filter = responseFilter.toLowerCase();
    return responses.filter(r =>
      r.vendor_name?.toLowerCase().includes(filter) ||
      r.vendor_id?.toLowerCase().includes(filter)
    );
  }, [responses, responseFilter]);

  const totalResponsePages = Math.ceil(filteredResponses.length / responsesPerPage);
  const paginatedResponses = filteredResponses.slice(
    (responsePage - 1) * responsesPerPage,
    responsePage * responsesPerPage
  );

  // 取得動態欄位名稱（優先使用 Google Sheet 的欄位順序）
  const getResponseColumns = useMemo(() => {
    if (responses.length === 0) return [];

    // 優先使用同步時保存的 Google Sheet 欄位順序
    const savedHeaders = formData?.form?.response_headers;

    if (savedHeaders && savedHeaders.length > 0) {
      // 使用 Google Sheet 的欄位順序
      return savedHeaders.map(name => ({
        name,
        isAttachment: name.includes('附件') || name.includes('檔案') || name.includes('attachment') ||
                      name.toLowerCase().includes('file') || name.toLowerCase().includes('upload')
      }));
    }

    // 後備方案：從回覆資料中提取欄位
    // 如實呈現 Google Sheet 所有欄位，不過濾任何欄位
    const allKeys = new Set();
    responses.forEach(r => {
      const data = r.response_data || r.items?.[0] || {};
      Object.keys(data).forEach(key => {
        allKeys.add(key);
      });
    });
    return Array.from(allKeys).map(name => ({
      name,
      isAttachment: name.includes('附件') || name.includes('檔案') || name.includes('attachment') ||
                    name.toLowerCase().includes('file') || name.toLowerCase().includes('upload')
    }));
  }, [responses, formData]);

  // 計算可指派的供應商（尚未指派的）
  const assignedVendorIds = new Set(assignments.map(a => a.vendor_id));
  const availableVendors = allVendors.filter(v => !assignedVendorIds.has(v.id));

  // 建立指派
  const createAssignmentsMutation = useMutation({
    mutationFn: (data) => projectFormsAPI.createAssignments(projectId, formId, data),
    onSuccess: (result, variables) => {
      toast.success(result.message || '指派成功');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms', formId, 'assignments'] });
      setShowAssignModal(false);
      setShowBatchAssign(false);
      // 指派完成後引導設定提醒（批量視窗預選剛指派的廠商）
      const assignedIds = variables?.vendor_ids || [];
      if (assignedIds.length > 0 && confirm(`已指派 ${assignedIds.length} 家供應商，是否現在設定提醒？`)) {
        setBulkReminderVendorIds(assignedIds);
      }
    },
    onError: (err) => toast.error(err.message || '指派失敗'),
  });

  // 更新指派設定
  const updateAssignmentMutation = useMutation({
    mutationFn: ({ vendorId, data }) => projectFormsAPI.updateAssignment(projectId, formId, vendorId, data),
    onSuccess: () => {
      toast.success('設定已更新');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms', formId, 'assignments'] });
      setShowReminderModal(false);
      setEditingVendorId(null);
    },
    onError: (err) => toast.error(err.message || '更新失敗'),
  });

  // 批量更新提醒設定
  const bulkRemindersMutation = useMutation({
    mutationFn: ({ vendorIds, data }) => projectFormsAPI.bulkUpdateReminders(projectId, formId, vendorIds, data),
    onSuccess: (result) => {
      toast.success(result.message || '批量提醒設定完成');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms', formId, 'assignments'] });
      setBulkReminderVendorIds(null);
      setSelectedVendors(new Set());
    },
    onError: (err) => toast.error(err.message || '批量設定失敗'),
  });

  // 移除指派
  const removeAssignmentMutation = useMutation({
    mutationFn: (vendorId) => projectFormsAPI.removeAssignment(projectId, formId, vendorId),
    onSuccess: () => {
      toast.success('已移除指派');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms', formId, 'assignments'] });
    },
    onError: (err) => toast.error(err.message || '移除失敗'),
  });

  // 批量發送邀請
  const sendInvitationsMutation = useMutation({
    mutationFn: (vendorIds) => projectFormsAPI.sendInvitations(projectId, formId, vendorIds),
    onSuccess: (result) => {
      toast.success(result.message || '發送完成');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms', formId, 'assignments'] });
      setSelectedVendors(new Set());
    },
    onError: (err) => toast.error(err.message || '發送失敗'),
  });

  // 要求重填：先把目前答案改為舊版本（並留下操作紀錄），再寄出新的問卷連結
  const resendMutation = useMutation({
    mutationFn: async (response) => {
      await projectResponsesAPI.resend(projectId, response.id);
      return projectFormsAPI.sendInvitations(projectId, formId, [response.vendor_id]);
    },
    onSuccess: (result) => {
      toast.success(`已要求重新填寫。${result?.message || ''}`);
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms', formId, 'assignments'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'responses', { formId }] });
    },
    onError: (err) => toast.error(err.message || '要求重填失敗'),
  });

  // 刪除回覆
  const deleteResponseMutation = useMutation({
    mutationFn: (responseId) => projectResponsesAPI.delete(projectId, responseId),
    onSuccess: () => {
      toast.success('回覆已刪除');
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'responses', { formId }] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId, 'forms', formId, 'assignments'] });
    },
    onError: (err) => toast.error(err.message || '刪除失敗'),
  });

  const handleSelectAll = () => {
    if (selectedVendors.size === filteredAssignments.length) {
      setSelectedVendors(new Set());
    } else {
      setSelectedVendors(new Set(filteredAssignments.map(a => a.vendor_id)));
    }
  };

  const handleSelectVendor = (vendorId) => {
    const newSelected = new Set(selectedVendors);
    if (newSelected.has(vendorId)) {
      newSelected.delete(vendorId);
    } else {
      newSelected.add(vendorId);
    }
    setSelectedVendors(newSelected);
  };

  const handleSendSelected = () => {
    if (selectedVendors.size === 0) {
      toast.error('請先選擇供應商');
      return;
    }
    if (!confirm(`確定要發送問卷給選中的 ${selectedVendors.size} 家供應商嗎？`)) return;
    sendInvitationsMutation.mutate([...selectedVendors]);
  };

  const handleSendSingleInvitation = (vendorId, vendorName) => {
    if (!confirm(`確定要發送問卷邀請給「${vendorName}」嗎？`)) return;
    sendInvitationsMutation.mutate([vendorId]);
  };

  const handleRemoveAssignment = (vendorId, vendorName) => {
    if (!confirm(`確定要移除「${vendorName}」的指派嗎？`)) return;
    removeAssignmentMutation.mutate(vendorId);
  };

  const handleEditReminder = (vendorId) => {
    setEditingVendorId(vendorId);
    setShowReminderModal(true);
  };

  const handleBulkReminders = () => {
    if (selectedVendors.size === 0) {
      toast.error('請先勾選供應商');
      return;
    }
    setBulkReminderVendorIds([...selectedVendors]);
  };

  // 未設定提醒的廠商（未填寫且定期催辦未啟用）
  const vendorsWithoutReminder = assignments.filter(
    (a) => a.fill_status !== 'completed' && !a.reminder_enabled
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-TW');
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-TW');
  };

  const handleDeleteResponse = (responseId, vendorName) => {
    if (!confirm(`確定要刪除「${vendorName}」的回覆嗎？刪除後將不再顯示、不再寄送提醒（系統仍保留紀錄）。`)) return;
    deleteResponseMutation.mutate(responseId);
  };

  const handleResendInvitation = (response) => {
    if (!confirm(`確定要求「${response.vendor_name}」重新填寫嗎？\n目前的答案會保留為舊版本（不計分，可在「回覆追蹤」查看），並寄出新的問卷連結。`)) return;
    // 需有指派才能寄出新連結
    const assignment = assignments.find(a => a.vendor_id === response.vendor_id);
    if (assignment) {
      resendMutation.mutate(response);
    } else {
      toast.error('找不到此供應商的指派');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">已完成</span>;
      case 'sent':
        return <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">已發送</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">待處理</span>;
    }
  };

  if (assignmentsLoading) {
    return (
      <div className="p-4 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* 操作列 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            已指派供應商 ({assignments.length})
          </span>
          {selectedVendors.size > 0 && (
            <span className="text-xs text-purple-600">
              已選 {selectedVendors.size} 家
            </span>
          )}
          {vendorsWithoutReminder.length > 0 && (
            <button
              onClick={() => setBulkReminderVendorIds(vendorsWithoutReminder.map(a => a.vendor_id))}
              className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 transition"
              title="點擊為這些廠商批量設定提醒"
            >
              ⚠ {vendorsWithoutReminder.length} 家未設定提醒
            </button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* 批量指派 */}
          <button
            onClick={() => setShowBatchAssign(true)}
            className="px-3 py-1.5 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition"
          >
            批量指派
          </button>

          {/* 批量設定提醒 */}
          <button
            onClick={handleBulkReminders}
            disabled={selectedVendors.size === 0}
            className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
            title={selectedVendors.size === 0 ? '請先勾選供應商' : ''}
          >
            {selectedVendors.size > 0 ? `批量設定提醒 (${selectedVendors.size})` : '批量設定提醒'}
          </button>

          {/* 批量發送 */}
          <button
            onClick={handleSendSelected}
            disabled={sendInvitationsMutation.isPending || selectedVendors.size === 0}
            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendInvitationsMutation.isPending ? '發送中...' : selectedVendors.size > 0 ? `批量發送邀請 (${selectedVendors.size})` : '批量發送邀請'}
          </button>
        </div>
      </div>

      {/* 篩選列 */}
      {assignments.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <input
            type="text"
            placeholder="搜尋供應商名稱、信箱、採購人員..."
            value={assignmentSearch}
            onChange={(e) => { setAssignmentSearch(e.target.value); setAssignmentPage(1); }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 w-64"
          />
          <select
            value={assignmentStatusFilter}
            onChange={(e) => { setAssignmentStatusFilter(e.target.value); setAssignmentPage(1); }}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          >
            <option value="">所有狀態</option>
            <option value="pending">待處理</option>
            <option value="sent">已發送</option>
            <option value="completed">已完成</option>
          </select>
          {(assignmentSearch || assignmentStatusFilter) && (
            <span className="text-xs text-gray-500">
              篩選結果：{filteredAssignments.length} 筆
            </span>
          )}
          <button
            onClick={() => {
              const statusMap = { pending: '待處理', sent: '已發送', completed: '已完成' };
              const bom = '\uFEFF';
              const headers = ['供應商', '信箱', '採購人員', '事業部', '截止日期', '截止提醒', '定期提醒', '狀態'];
              const rows = filteredAssignments.map(a => [
                a.vendor_name || '',
                a.vendor_email || '',
                a.buyer || '',
                a.business_unit || '',
                a.deadline ? new Date(a.deadline).toLocaleDateString('zh-TW') : '',
                [a.reminder_days_before > 0 ? `${a.reminder_days_before}天前` : '', a.remind_on_deadline ? '當天' : ''].filter(Boolean).join('+') || '',
                describeReminder(a) || '',
                statusMap[a.fill_status] || '待處理',
              ]);
              const csv = bom + [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `已指派供應商_${formName || 'export'}.csv`;
              link.click();
              URL.revokeObjectURL(url);
            }}
            className="ml-auto px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            下載 CSV
          </button>
        </div>
      )}

      {/* 已指派供應商列表 */}
      {assignments.length === 0 ? (
        <div className="text-center py-6 bg-gray-50 rounded-lg">
          <p className="text-gray-500 text-sm">尚未指派供應商</p>
          <button
            onClick={() => setShowAssignModal(true)}
            className="mt-2 text-sm text-purple-600 hover:text-purple-800 underline"
          >
            + 指派供應商
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* 表格 */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input
                      type="checkbox"
                      checked={selectedVendors.size === filteredAssignments.length && filteredAssignments.length > 0}
                      onChange={handleSelectAll}
                      className="rounded text-purple-600"
                    />
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 min-w-[120px]">供應商</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 min-w-[80px]">採購人員</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 min-w-[80px]">事業部</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 min-w-[90px]">截止日期</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 min-w-[100px]">截止提醒</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 min-w-[80px]">定期提醒</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 min-w-[60px]">狀態</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 min-w-[100px]">操作</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAssignments.map((assignment) => (
                  <tr
                    key={assignment.vendor_id}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${
                      selectedVendors.has(assignment.vendor_id) ? 'bg-purple-50' : ''
                    }`}
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedVendors.has(assignment.vendor_id)}
                        onChange={() => handleSelectVendor(assignment.vendor_id)}
                        className="rounded text-purple-600"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <p className="text-sm font-medium text-gray-900 truncate">{assignment.vendor_name}</p>
                      <p className="text-xs text-gray-500 truncate">{assignment.vendor_email}</p>
                      {assignment.email_status === 'bounced' && (
                        <span
                          className="inline-block mt-0.5 px-1.5 py-0.5 bg-red-100 text-red-700 rounded text-xs"
                          title={`此信箱退信，請確認地址後重寄。${assignment.email_bounce_reason || ''}`}
                        >
                          ❌ Email 退信
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 truncate" title={assignment.buyer}>
                      {assignment.buyer || '-'}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-700 truncate" title={assignment.business_unit}>
                      {assignment.business_unit || '-'}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      {assignment.deadline ? (
                        <span className={new Date(assignment.deadline) < new Date() ? 'text-red-600' : 'text-gray-700'}>
                          {formatDate(assignment.deadline)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {assignment.deadline && (
                        <span>
                          {assignment.reminder_days_before > 0 && `${assignment.reminder_days_before}天前`}
                          {assignment.reminder_days_before > 0 && assignment.remind_on_deadline && ' + '}
                          {assignment.remind_on_deadline && '當天'}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {describeReminder(assignment) ? (
                        <span className="text-purple-600" title={describeReminder(assignment)}>
                          {describeReminder(assignment)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {getStatusBadge(assignment.fill_status)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleSendSingleInvitation(assignment.vendor_id, assignment.vendor_name)}
                          disabled={sendInvitationsMutation.isPending || assignment.fill_status === 'completed'}
                          className={`p-1 rounded ${
                            assignment.fill_status === 'completed'
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                          title="發送問卷邀請"
                        >
                          <span className="text-xs">📤</span>
                        </button>
                        <button
                          onClick={() => handleEditReminder(assignment.vendor_id)}
                          className={`p-1 rounded ${
                            assignment.reminder_enabled
                              ? 'text-purple-600 bg-purple-50'
                              : 'text-gray-500 hover:text-purple-600 hover:bg-purple-50'
                          }`}
                          title="截止與提醒設定"
                        >
                          <span className="text-xs">🔔</span>
                        </button>
                        <button
                          onClick={() => handleRemoveAssignment(assignment.vendor_id, assignment.vendor_name)}
                          disabled={removeAssignmentMutation.isPending}
                          className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="移除指派"
                        >
                          <span className="text-xs">🗑️</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* 已指派供應商分頁 */}
          {totalAssignmentPages > 1 && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50">
              <div className="text-xs text-gray-500">
                顯示 {(assignmentPage - 1) * assignmentsPerPage + 1}-{Math.min(assignmentPage * assignmentsPerPage, filteredAssignments.length)} / {filteredAssignments.length} 筆
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setAssignmentPage(p => Math.max(1, p - 1))}
                  disabled={assignmentPage === 1}
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  上一頁
                </button>
                <span className="px-2 py-1 text-xs text-gray-600">
                  {assignmentPage} / {totalAssignmentPages}
                </span>
                <button
                  onClick={() => setAssignmentPage(p => Math.min(totalAssignmentPages, p + 1))}
                  disabled={assignmentPage === totalAssignmentPages}
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                >
                  下一頁
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 新增供應商按鈕 */}
      {availableVendors.length > 0 && (
        <button
          onClick={() => setShowAssignModal(true)}
          className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition"
        >
          + 新增供應商指派 (可選 {availableVendors.length} 家)
        </button>
      )}

      {/* 問卷回覆區域 */}
      <div className="border-t border-gray-200 mt-4 pt-4">
        <div
          className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-3 py-2 rounded-lg transition"
          onClick={() => setShowResponses(!showResponses)}
        >
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs">
              {showResponses ? '▼' : '▶'}
            </span>
            <span className="text-sm font-medium text-gray-700">
              問卷回覆
            </span>
            <span className="text-xs text-gray-500">
              ({responses.length} 筆)
            </span>
          </div>
        </div>

        {showResponses && (
          <div className="mt-3">
            {responses.length === 0 ? (
              <div className="p-6 text-center text-gray-500 bg-gray-50 rounded-lg">
                <p>尚無回覆資料</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* 搜尋和統計 */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <input
                    type="text"
                    placeholder="搜尋供應商..."
                    value={responseFilter}
                    onChange={(e) => {
                      setResponseFilter(e.target.value);
                      setResponsePage(1);
                    }}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 w-48"
                  />
                  <div className="text-sm text-gray-600">
                    共 {getResponseColumns.length} 個欄位 ·
                    顯示 {Math.min((responsePage - 1) * responsesPerPage + 1, filteredResponses.length)}-{Math.min(responsePage * responsesPerPage, filteredResponses.length)} / {filteredResponses.length} 筆
                  </div>
                </div>

                {/* 回覆表格 */}
                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                  <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                    <table className="w-full border-collapse">
                      <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-300 sticky left-0 bg-gray-100 z-20 min-w-[100px]">
                            供應商
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-300 min-w-[140px]">
                            提交時間
                          </th>
                          {getResponseColumns.map((col) => (
                            <th
                              key={col.name}
                              className={`px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-300 min-w-[180px] max-w-[350px] ${
                                col.isAttachment ? 'bg-green-50' : ''
                              }`}
                              title={col.name}
                            >
                              <div className="truncate">{col.name}</div>
                            </th>
                          ))}
                          <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-300 sticky right-0 bg-gray-100 z-20 min-w-[80px]">
                            操作
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedResponses.map((response, idx) => {
                          const responseData = response.response_data || response.items?.[0] || {};
                          return (
                            <tr
                              key={response.id}
                              className={`border-b border-gray-100 hover:bg-purple-50/30 transition ${
                                idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                              }`}
                            >
                              <td className="px-3 py-2 text-xs font-medium text-gray-900 sticky left-0 z-10 bg-inherit border-r border-gray-100">
                                {response.vendor_name || 'N/A'}
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100 whitespace-nowrap">
                                {formatDateTime(response.submitted_at)}
                              </td>
                              {getResponseColumns.map((col) => {
                                if (col.isAttachment) {
                                  const attachment = response.attachments?.find(att => att.field_name === col.name);
                                  return (
                                    <td key={col.name} className="px-3 py-2 text-xs text-gray-700 border-r border-gray-100 bg-green-50/50">
                                      {attachment ? (
                                        <a
                                          href={attachment.view_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 underline"
                                        >
                                          查看檔案
                                        </a>
                                      ) : responseData[col.name] ? (
                                        <a
                                          href={responseData[col.name]}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-600 hover:text-blue-800 underline"
                                        >
                                          查看檔案
                                        </a>
                                      ) : '-'}
                                    </td>
                                  );
                                } else {
                                  const cellValue = responseData[col.name] || '-';

                                  // 將 cellValue 拆分成多段，逐段判斷是否為 URL
                                  const renderCellContent = (value) => {
                                    if (typeof value !== 'string' || value === '-') return value;
                                    // 用 ", " 或換行分隔
                                    const segments = value.split(/,\s*|\n/).filter(Boolean);
                                    if (segments.length <= 1) {
                                      const trimmed = value.trim();
                                      if (/^https?:\/\//.test(trimmed)) {
                                        return (
                                          <a
                                            href={trimmed}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 underline"
                                          >
                                            {trimmed.includes('drive.google.com') ? '查看檔案' : trimmed}
                                          </a>
                                        );
                                      }
                                      return value;
                                    }
                                    let urlIndex = 0;
                                    return segments.map((seg, i) => {
                                      const trimmed = seg.trim();
                                      const isUrl = /^https?:\/\//.test(trimmed);
                                      if (isUrl) urlIndex++;
                                      return (
                                        <span key={i}>
                                          {i > 0 && <br />}
                                          {isUrl ? (
                                            <a
                                              href={trimmed}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:text-blue-800 underline"
                                            >
                                              {trimmed.includes('drive.google.com') ? `查看檔案 (${urlIndex})` : trimmed}
                                            </a>
                                          ) : trimmed}
                                        </span>
                                      );
                                    });
                                  };

                                  return (
                                    <td key={col.name} className="px-3 py-2 text-xs text-gray-700 border-r border-gray-100" title={cellValue}>
                                      <div className="max-w-[350px] whitespace-pre-wrap break-words">
                                        {renderCellContent(cellValue)}
                                      </div>
                                    </td>
                                  );
                                }
                              })}
                              <td className="px-3 py-2 text-xs whitespace-nowrap sticky right-0 z-10 bg-inherit border-l border-gray-100">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleResendInvitation(response)}
                                    disabled={resendMutation.isPending}
                                    className="text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50"
                                    title="要求重新填寫"
                                  >
                                    重填
                                  </button>
                                  <button
                                    onClick={() => handleDeleteResponse(response.id, response.vendor_name)}
                                    disabled={deleteResponseMutation.isPending}
                                    className="text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                                  >
                                    刪除
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* 分頁 */}
                {totalResponsePages > 1 && (
                  <div className="flex justify-center items-center gap-2">
                    <button
                      onClick={() => setResponsePage(p => Math.max(1, p - 1))}
                      disabled={responsePage === 1}
                      className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      上一頁
                    </button>
                    <span className="text-sm text-gray-600">
                      第 {responsePage} / {totalResponsePages} 頁
                    </span>
                    <button
                      onClick={() => setResponsePage(p => Math.min(totalResponsePages, p + 1))}
                      disabled={responsePage === totalResponsePages}
                      className="px-3 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      下一頁
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 批量指派 Modal */}
      {showBatchAssign && (
        <BatchAssignDropdown
          tags={tags}
          availableVendors={availableVendors}
          onAssign={(vendorIds, deadline, reminderDays, remindOnDeadline) => {
            createAssignmentsMutation.mutate({
              vendor_ids: vendorIds,
              deadline,
              reminder_days_before: reminderDays,
              remind_on_deadline: remindOnDeadline,
            });
            setShowBatchAssign(false);
          }}
          onClose={() => setShowBatchAssign(false)}
          isLoading={createAssignmentsMutation.isPending}
        />
      )}

      {/* 指派供應商 Modal */}
      {showAssignModal && (
        <AssignVendorsModal
          availableVendors={availableVendors}
          onAssign={(vendorIds, deadline, reminderDays, remindOnDeadline) => {
            createAssignmentsMutation.mutate({
              vendor_ids: vendorIds,
              deadline,
              reminder_days_before: reminderDays,
              remind_on_deadline: remindOnDeadline,
            });
          }}
          onClose={() => setShowAssignModal(false)}
          isLoading={createAssignmentsMutation.isPending}
        />
      )}

      {/* 截止與提醒設定 Modal（單一廠商） */}
      {showReminderModal && editingVendorId && (
        <ReminderSettingsModal
          assignment={assignments.find(a => a.vendor_id === editingVendorId)}
          formName={formName}
          onSave={(data) => updateAssignmentMutation.mutate({ vendorId: editingVendorId, data })}
          onClose={() => { setShowReminderModal(false); setEditingVendorId(null); }}
          isLoading={updateAssignmentMutation.isPending}
        />
      )}

      {/* 批量設定提醒 Modal */}
      {bulkReminderVendorIds && (
        <BulkReminderModal
          vendorIds={bulkReminderVendorIds}
          assignments={assignments}
          formName={formName}
          onSave={(vendorIds, data) => bulkRemindersMutation.mutate({ vendorIds, data })}
          onClose={() => setBulkReminderVendorIds(null)}
          isLoading={bulkRemindersMutation.isPending}
        />
      )}
    </div>
  );
}

/**
 * 批量指派下拉選單
 * 點擊標籤後顯示該標籤的供應商，預設全選，可取消勾選
 */
function BatchAssignDropdown({ tags, availableVendors, onAssign, onClose, isLoading }) {
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedVendorIds, setSelectedVendorIds] = useState(new Set());
  const [deadline, setDeadline] = useState('');
  const [reminderDays, setReminderDays] = useState(1);
  const [remindOnDeadline, setRemindOnDeadline] = useState(true);

  // 取得選中標籤的可用供應商
  const vendorsWithTag = selectedTag
    ? availableVendors.filter(v => v.tags?.includes(selectedTag))
    : [];

  // 當選擇標籤時，預設全選該標籤的供應商
  const handleTagClick = (tagName) => {
    if (selectedTag === tagName) {
      // 再次點擊取消選擇
      setSelectedTag(null);
      setSelectedVendorIds(new Set());
    } else {
      setSelectedTag(tagName);
      const vendorIds = availableVendors
        .filter(v => v.tags?.includes(tagName))
        .map(v => v.id);
      setSelectedVendorIds(new Set(vendorIds));
    }
  };

  const handleVendorToggle = (vendorId) => {
    const newSelected = new Set(selectedVendorIds);
    if (newSelected.has(vendorId)) {
      newSelected.delete(vendorId);
    } else {
      newSelected.add(vendorId);
    }
    setSelectedVendorIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedVendorIds.size === vendorsWithTag.length) {
      setSelectedVendorIds(new Set());
    } else {
      setSelectedVendorIds(new Set(vendorsWithTag.map(v => v.id)));
    }
  };

  const handleSubmit = () => {
    if (selectedVendorIds.size === 0) {
      toast.error('請選擇至少一個供應商');
      return;
    }
    onAssign(
      [...selectedVendorIds],
      deadline || null,
      reminderDays,
      remindOnDeadline
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h4 className="font-medium text-gray-800">依標籤批量指派</h4>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
        {/* 標籤選擇 */}
        <p className="text-xs text-gray-500 mb-2">點選標籤查看供應商：</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {tags.length === 0 ? (
            <p className="text-xs text-gray-500">尚無標籤</p>
          ) : (
            tags.map((tag) => {
              const count = availableVendors.filter(v => v.tags?.includes(tag.name)).length;
              return (
                <button
                  key={tag.id}
                  onClick={() => handleTagClick(tag.name)}
                  className={`px-2 py-1 rounded text-xs transition ${
                    selectedTag === tag.name
                      ? 'ring-2 ring-purple-500'
                      : 'hover:opacity-80'
                  }`}
                  style={{ backgroundColor: tag.color + '20', color: tag.color }}
                >
                  {tag.name} ({count})
                </button>
              );
            })
          )}
        </div>

        {/* 供應商列表（選擇標籤後顯示） */}
        {selectedTag && (
          <div className="border border-gray-200 rounded-lg mb-3">
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedVendorIds.size === vendorsWithTag.length && vendorsWithTag.length > 0}
                  onChange={handleSelectAll}
                  className="rounded text-purple-600"
                />
                <span className="text-xs font-medium text-gray-700">
                  全選 ({selectedVendorIds.size}/{vendorsWithTag.length})
                </span>
              </label>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {vendorsWithTag.length === 0 ? (
                <p className="p-3 text-center text-xs text-gray-500">此標籤無可指派供應商</p>
              ) : (
                vendorsWithTag.map((vendor) => (
                  <label
                    key={vendor.id}
                    className={`flex items-center gap-2 px-3 py-2 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                      selectedVendorIds.has(vendor.id) ? 'bg-purple-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedVendorIds.has(vendor.id)}
                      onChange={() => handleVendorToggle(vendor.id)}
                      className="rounded text-purple-600"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{vendor.name}</p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>
        )}

        {/* 截止日期與提醒（選擇供應商後顯示） */}
        {selectedVendorIds.size > 0 && (
          <>
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">截止日期</label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
              />
            </div>

            <div className="mb-3 flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="30"
                value={reminderDays}
                onChange={(e) => setReminderDays(parseInt(e.target.value) || 0)}
                className="w-12 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500"
              />
              <span className="text-xs text-gray-600">天前提醒</span>
              <label className="flex items-center gap-1 ml-2">
                <input
                  type="checkbox"
                  checked={remindOnDeadline}
                  onChange={(e) => setRemindOnDeadline(e.target.checked)}
                  className="rounded text-purple-600"
                />
                <span className="text-xs text-gray-600">截止當天</span>
              </label>
            </div>

            <p className="mb-3 text-xs text-gray-500">
              定期催辦提醒可於指派完成後批量設定
            </p>

            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 disabled:opacity-50 transition"
            >
              {isLoading ? '指派中...' : `確認指派 ${selectedVendorIds.size} 家`}
            </button>
          </>
        )}
      </div>
    </div>
    </div>
  );
}

/**
 * 指派供應商 Modal
 */
function AssignVendorsModal({ availableVendors, onAssign, onClose, isLoading }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deadline, setDeadline] = useState('');
  const [reminderDays, setReminderDays] = useState(1);
  const [remindOnDeadline, setRemindOnDeadline] = useState(true);
  const [searchText, setSearchText] = useState('');

  const filteredVendors = availableVendors.filter(v =>
    v.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    v.contact_email?.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleToggle = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSubmit = () => {
    if (selectedIds.size === 0) {
      toast.error('請選擇至少一個供應商');
      return;
    }
    onAssign(
      [...selectedIds],
      deadline || null,
      reminderDays,
      remindOnDeadline
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">指派供應商</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 搜尋 */}
        <input
          type="text"
          placeholder="搜尋供應商..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-purple-500"
        />

        {/* 供應商列表 */}
        <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg mb-4">
          {filteredVendors.length === 0 ? (
            <p className="p-4 text-center text-gray-500 text-sm">無符合的供應商</p>
          ) : (
            filteredVendors.map((vendor) => (
              <label
                key={vendor.id}
                className={`flex items-center gap-3 p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                  selectedIds.has(vendor.id) ? 'bg-purple-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(vendor.id)}
                  onChange={() => handleToggle(vendor.id)}
                  className="rounded text-purple-600"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{vendor.name}</p>
                  <p className="text-xs text-gray-500 truncate">{vendor.contact_email || '-'}</p>
                </div>
                {vendor.tags?.length > 0 && (
                  <div className="flex gap-1">
                    {vendor.tags.slice(0, 2).map((tag, i) => (
                      <span key={i} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </label>
            ))
          )}
        </div>

        {/* 截止日期和提醒設定 */}
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">截止日期（選填）</label>
            <input
              type="date"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="30"
                value={reminderDays}
                onChange={(e) => setReminderDays(parseInt(e.target.value) || 0)}
                className="w-16 px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-600">天前提醒</span>
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={remindOnDeadline}
                onChange={(e) => setRemindOnDeadline(e.target.checked)}
                className="rounded text-purple-600"
              />
              <span className="text-sm text-gray-600">截止當天提醒</span>
            </label>
          </div>

          <p className="text-xs text-gray-500">
            定期催辦提醒可於指派完成後批量設定
          </p>
        </div>

        {/* 按鈕 */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || selectedIds.size === 0}
            className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {isLoading ? '指派中...' : `指派 ${selectedIds.size} 家`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 截止與提醒設定 Modal（單一廠商，統一視窗）
 */
function ReminderSettingsModal({ assignment, formName, onSave, onClose, isLoading }) {
  const [state, setState] = useState(() => buildReminderState(assignment));

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-TW');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { payload, error } = reminderStateToPayload(state);
    if (error) {
      toast.error(error);
      return;
    }
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">截止與提醒設定</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 供應商和問卷資訊 */}
        <div className="mb-4 p-3 bg-purple-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">{assignment?.vendor_name}</p>
          <p className="text-xs text-gray-600">{formName}</p>
          {assignment?.next_reminder_date && (
            <p className="text-xs text-purple-600 mt-1">
              下次提醒：{formatDate(assignment.next_reminder_date)}
            </p>
          )}
          {assignment?.last_reminder_date && (
            <p className="text-xs text-gray-500">
              上次提醒：{formatDate(assignment.last_reminder_date)}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <ReminderScheduleEditor state={state} onChange={setState} />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {isLoading ? '儲存中...' : '確認'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * 批量設定提醒 Modal
 * 對已勾選（或指派完成後預選）的廠商套用同一組提醒設定
 */
function BulkReminderModal({ vendorIds, assignments, formName, onSave, onClose, isLoading }) {
  const targetAssignments = assignments.filter(a => vendorIds.includes(a.vendor_id));
  // 批量預設值：不帶入任何單一廠商的既有設定，從乾淨狀態開始
  const [state, setState] = useState(() => buildReminderState(null));
  // 批量模式預設不改截止日（保留各廠商原設定），勾選後才一併套用
  const [applyDeadline, setApplyDeadline] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const { payload, error } = reminderStateToPayload(state, { includeDeadline: applyDeadline });
    if (error) {
      toast.error(error);
      return;
    }
    onSave(vendorIds, payload);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">批量設定提醒</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 套用對象 */}
        <div className="mb-4 p-3 bg-purple-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">
            將套用至 {targetAssignments.length} 家供應商
          </p>
          <p className="text-xs text-gray-600 truncate" title={targetAssignments.map(a => a.vendor_name).join('、')}>
            {targetAssignments.slice(0, 5).map(a => a.vendor_name).join('、')}
            {targetAssignments.length > 5 && ` 等 ${targetAssignments.length} 家`}
          </p>
          <p className="text-xs text-gray-500 mt-1">{formName}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 批量截止日：預設保留各廠商原設定 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={applyDeadline}
              onChange={(e) => setApplyDeadline(e.target.checked)}
              className="rounded text-purple-600"
            />
            <span className="text-sm text-gray-700">同時更新截止日期（不勾選則保留各廠商原截止日）</span>
          </label>

          <ReminderScheduleEditor state={state} onChange={setState} showDeadline={applyDeadline} />

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isLoading || targetAssignments.length === 0}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {isLoading ? '儲存中...' : `套用至 ${targetAssignments.length} 家`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * 設定定期提醒 Modal
 */
function PeriodicReminderModal({ assignment, formName, onSave, onClose, isLoading }) {
  const [frequency, setFrequency] = useState(assignment?.frequency || 'weekly');
  const [frequencyValue, setFrequencyValue] = useState(assignment?.frequency_value || 1);
  const [reminderEnabled, setReminderEnabled] = useState(assignment?.reminder_enabled ?? false);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-TW');
  };

  const getFrequencyText = () => {
    const unit = frequency === 'daily' ? '天' : frequency === 'weekly' ? '週' : '月';
    return `每 ${frequencyValue} ${unit}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      reminder_type: reminderEnabled ? 'periodic' : null,
      frequency,
      frequency_value: frequencyValue,
      reminder_enabled: reminderEnabled,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            設定定期提醒
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded transition">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 供應商和問卷資訊 */}
        <div className="mb-4 p-3 bg-purple-50 rounded-lg">
          <p className="text-sm font-medium text-gray-900">{assignment?.vendor_name}</p>
          <p className="text-xs text-gray-600">{formName}</p>
          {assignment?.next_reminder_date && (
            <p className="text-xs text-purple-600 mt-1">
              下次提醒：{formatDate(assignment.next_reminder_date)}
            </p>
          )}
          {assignment?.last_reminder_date && (
            <p className="text-xs text-gray-500">
              上次提醒：{formatDate(assignment.last_reminder_date)}
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 啟用定期提醒 */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={reminderEnabled}
              onChange={(e) => setReminderEnabled(e.target.checked)}
              className="rounded text-purple-600 w-5 h-5"
            />
            <span className="text-sm font-medium text-gray-700">啟用定期提醒</span>
          </label>

          {reminderEnabled && (
            <>
              {/* 提醒頻率 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">提醒頻率</label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="daily">每日</option>
                  <option value="weekly">每週</option>
                  <option value="monthly">每月</option>
                </select>
              </div>

              {/* 間隔數值 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">間隔</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={frequencyValue}
                    onChange={(e) => setFrequencyValue(parseInt(e.target.value) || 1)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-600">
                    {frequency === 'daily' ? '天' : frequency === 'weekly' ? '週' : '月'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {getFrequencyText()}提醒一次
                </p>
              </div>

              {/* 預覽 */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600">
                  系統將{getFrequencyText()}自動發送提醒郵件給此供應商，直到供應商完成填寫或您關閉此設定。
                </p>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {isLoading ? '儲存中...' : '確認'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
